import {
  Alert,
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { BackForeColor } from "../../../components/common/BackForeColor";
import TotalValueItem from "../../event/components/TotalValueItem";
import PaymentIcon from "../../event/components/PaymentIcon";
import BillIcon from "../../event/components/BillIcon";
import localize from "../../../common/localize";
import { Delete, Edit } from "@mui/icons-material";

const InfoRow = ({ label, children }) => (
  <Box className="flex items-start gap-2" sx={{ minHeight: "20px" }}>
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontWeight: 700,
        minWidth: "110px",
        color: "#374151",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Box className="flex-1" sx={{ fontSize: "0.75rem", color: "#111827" }}>
      {children}
    </Box>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography
    sx={{
      fontSize: "0.7rem",
      fontWeight: 700,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      color: "#6B7280",
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

export default function DailyRoutEventItem({
  item,
  onEditObservation,
  onDelete,
}) {
  const event = item?.EventInfo;
  const isCancelled = event?.OperationalStatus === 0;
  const clientName = event?.ReporterName ?? event?.Client?.Name;
  const address = event?.Address
    ? `${event?.Address} ${event?.AddressComplement ?? ""}`.trim()
    : event?.Client?.Address;
  const phone = event?.ReporterPhoneNumber ?? event?.Client?.PhoneNumber;
  const assignedTo = event?.PersonalAssigned
    ? `${event.PersonalAssigned.SurName} ${event.PersonalAssigned.LastName}`
    : localize.no_data;
  const creationDate = event?.CreationDate
    ? new Date(event.CreationDate).toLocaleString(undefined, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : localize.no_data;

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        p: 1.5,
        borderRadius: "10px",
        boxShadow:
          "rgba(60, 64, 67, 0.15) 0px 1px 2px 0px, rgba(60, 64, 67, 0.08) 0px 1px 3px 1px",
      }}
    >
      {/* HEADER: actions + code link + proceeding */}
      <Box className="flex justify-between items-center gap-2">
        <Box className="flex items-center gap-2">
          {onDelete && (
            <Tooltip title={localize.delete ?? "Eliminar"}>
              <IconButton size="small" onClick={onDelete}>
                <Delete sx={{ color: "#DC2626", width: 18, height: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
            {`${localize.code}:`}
          </Typography>
          <Link
            sx={{ fontSize: "0.85rem", fontWeight: 600 }}
            color="primary"
            underline="always"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.open(`/events/detail/${event?.Id}`, "_blank");
            }}
          >
            {`${event?.Code ?? ""}`}
          </Link>
        </Box>

        {event?.ProceedingInfo?.Name && (
          <Chip
            label={event.ProceedingInfo.Name}
            size="small"
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              backgroundColor: "#F3F4F6",
              color: "#374151",
            }}
          />
        )}
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* STATUS ROW: event type + execution status (full width each) */}
      <Stack spacing={0.75}>
        <Box className="flex items-center gap-2 w-full">
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "#6B7280",
              minWidth: "140px",
              whiteSpace: "nowrap",
            }}
          >
            {`${localize.event_type}:`}
          </Typography>
          <Box sx={{ flex: 1, "& > *": { width: "100%" } }}>
            <BackForeColor
              styleName="text-xs w-full"
              backColor={event?.EventType?.BackgroundColor}
              foreColor={event?.EventType?.ForegroundColor}
              text={event?.EventType?.Name}
            />
          </Box>
        </Box>

        <Box className="flex items-center gap-2 w-full">
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "#6B7280",
              minWidth: "140px",
              whiteSpace: "nowrap",
            }}
          >
            {`${localize.execution_status}:`}
          </Typography>
          <Box sx={{ flex: 1, "& > *": { width: "100%" } }}>
            <BackForeColor
              backColor={
                isCancelled ? "white" : event?.CurrentStatus?.BackgroundColor
              }
              foreColor={
                isCancelled ? "red" : event?.CurrentStatus?.ForegroundColor
              }
              text={
                isCancelled ? localize.cancelled : event?.CurrentStatus?.Name
              }
              styleName={
                isCancelled
                  ? "cancelled_operational_status text-xs w-full"
                  : "text-xs w-full"
              }
            />
          </Box>
        </Box>
      </Stack>

      <Divider sx={{ my: 1 }} />

      {/* DATA: client info + assignment + financial summary stacked */}
      <Stack spacing={1.5}>
        <Box>
          <SectionTitle>
            {localize.client_information ?? localize.client}
          </SectionTitle>
          <Stack spacing={0.5}>
            <InfoRow label={`${localize.client}:`}>
              {clientName ?? localize.no_data}
            </InfoRow>
            <InfoRow label={`${localize.address}:`}>
              {address ?? localize.no_data}
            </InfoRow>
            <InfoRow label={`${localize.phone_number}:`}>
              {phone ?? localize.no_data}
            </InfoRow>
          </Stack>
        </Box>

        <Box>
          <SectionTitle>
            {localize.assignment ?? localize.assigned_to}
          </SectionTitle>
          <Stack spacing={0.5}>
            <InfoRow label={`${localize.assigned_to}:`}>{assignedTo}</InfoRow>
            <InfoRow label={`${localize.creation_date}:`}>
              {creationDate}
            </InfoRow>
          </Stack>
        </Box>

        <Box>
          <Divider/>
          <Stack
            direction="row"
            spacing={1}
            alignItems="stretch"
            sx={{
              flexWrap: { xs: "wrap", sm: "nowrap" }, marginTop: "10px"
            }}
          >
            <Box
              sx={{
                flex: 1,
                p: 1,
                borderRadius: "8px",
                backgroundColor: "#F9FAFB",
                border: "1px solid #E5E7EB",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  textAlign: "center"
                }}
              >
                {localize.total_value}
              </Typography>
              <TotalValueItem
                hideEditPlaceholder
                customStyle="text-sm font-bold"
                item={event}
              />
            </Box>

            <Box
              sx={{
                flex: 1,
                p: 1,
                borderRadius: "8px",
                backgroundColor: "#F9FAFB",
                border: "1px solid #E5E7EB",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {localize.payed}
              </Typography>
              <PaymentIcon item={event} />
            </Box>

            <Box
              sx={{
                flex: 1,
                p: 1,
                borderRadius: "8px",
                backgroundColor: "#F9FAFB",
                border: "1px solid #E5E7EB",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#6B7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {localize.billed}
              </Typography>
              <BillIcon item={event} />
            </Box>
          </Stack>
        </Box>
      </Stack>

      <Divider sx={{ my: 1 }} />

      {/* OBSERVATIONS */}
      <Box className="w-full flex gap-2 items-center">
        {onEditObservation && (
          <Tooltip title={localize.edit ?? "Editar"}>
            <IconButton size="small" onClick={onEditObservation}>
              <Edit sx={{ width: 18, height: 18, color: "#10366E" }} />
            </IconButton>
          </Tooltip>
        )}
        <Alert
          severity="info"
          className="w-full"
          sx={{ py: 0.5, "& .MuiAlert-message": { py: 0.5 } }}
        >
          <Typography sx={{ fontSize: "0.75rem" }}>
            <b>{`${localize.observations}: `}</b>
            {item?.Observation && item?.Observation?.length > 0
              ? item.Observation
              : localize.no_data}
          </Typography>
        </Alert>
      </Box>
    </Card>
  );
}
